import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, errorMessage, money } from "../../lib/api";
import { useBrand, useConfig } from "../../context/BrandContext";
import { LongDistanceNote } from "../LongDistanceNote";

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-slate-200 last:border-0 text-sm">
    <span className="text-slate-500">{k}</span><span className="text-slate-900 text-right" data-testid={testid}>{v}</span>
  </div>
);

const LOC = { residence: "Residence", mechanic: "Mechanic / shop", business: "Business", other: "Other" };

export const StepReview = ({ form }) => {
  const b = useBrand();
  const { item_types } = useConfig();
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");
  const it = form.item;

  useEffect(() => {
    api.post("/quote", {
      pickup_zip: form.pickup.zip, delivery_zip: form.delivery.zip, item_type: it.item_type,
      transport_type: it.transport_type, operable: it.item_type === "freight" ? true : it.operable, expedited: it.expedited,
    }).then((r) => setQuote(r.data)).catch((e) => setErr(errorMessage(e)));
  }, [form.pickup.zip, form.delivery.zip, it.item_type, it.transport_type, it.operable, it.expedited]);

  const label = item_types.find((t) => t.value === it.item_type)?.label;
  const itemText = it.item_type === "freight" ? `Freight: ${it.description}` : `${it.year} ${it.make} ${it.model}`;
  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="reg-step-review">
      <div className="lg:col-span-3 space-y-6">
        <div className="border border-slate-200 p-5">
          <div className="font-display uppercase tracking-wider font-bold text-brand-600 mb-2">Customer</div>
          <Row k="Name" v={form.customer.full_name} testid="review-name" />
          <Row k="Email" v={form.customer.email} testid="review-email" />
          <Row k="Phone" v={form.customer.phone} />
          {form.customer.company && <Row k="Company" v={form.customer.company} />}
        </div>
        <div className="border border-slate-200 p-5">
          <div className="font-display uppercase tracking-wider font-bold text-brand-600 mb-2">Shipment</div>
          <Row k="Type" v={label} />
          <Row k="Item" v={itemText} testid="review-item" />
          <Row k="Carrier" v={`${it.transport_type === "enclosed" ? "Enclosed" : "Open"}${it.expedited ? " · Expedited" : ""}${it.item_type !== "freight" && !it.operable ? " · Inoperable" : ""}`} />
          <Row k="Pickup" v={`${form.pickup.address}, ${form.pickup.zip}`} />
          <Row k="Delivery" v={`${form.delivery.address}, ${form.delivery.zip} (${LOC[form.delivery.location_type]})`} />
          <Row k="Preferred pickup" v={form.pickup_date} />
        </div>
      </div>
      <div className="lg:col-span-2 panel p-6 h-fit border-amber-500/40" data-testid="review-estimate">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Estimated price</div>
        {!quote && !err && <Loader2 className="w-6 h-6 animate-spin text-orange-600 mt-4" />}
        {err && <p className="mt-3 text-sm text-red-600" data-testid="review-estimate-error">{err}</p>}
        {quote && (
          <>
            <div className="mt-2 font-display font-black text-brand-600 text-5xl" data-testid="review-estimate-price">{money(quote.estimate)}</div>
            <div className="font-mono text-xs text-slate-600 mt-1" data-testid="review-estimate-rate">{quote.breakdown?.minimum_applied ? "Minimum charge" : `$${Number(quote.breakdown?.rate_per_mile || 0).toFixed(2)} per mile`}</div>
            <div className="mt-4 text-sm text-slate-700" data-testid="review-estimate-route">{quote.origin.city}, {quote.origin.state} → {quote.destination.city}, {quote.destination.state}</div>
            <div className="text-sm text-slate-600">{quote.miles.toLocaleString()} mi · {quote.transit_days_min}–{quote.transit_days_max} days transit</div>
            <LongDistanceNote quote={quote} testid="review-long-distance" />
          </>
        )}
        <p className="mt-5 text-xs text-slate-600 border-l-2 border-amber-500 pl-3 leading-relaxed">Estimate. Final price confirmed on your official invoice. {b.payment_note.split(". ")[0]}.</p>
      </div>
    </div>
  );
};
